import { useCallback, useEffect, useMemo, useRef } from 'react';
import debounce from 'lodash/debounce';

/**
 * useDescriptionReady
 * - Fires immediately once description reaches 7 words for the first time (on crossing threshold)
 * - Otherwise, waits for 2s of inactivity before firing
 * - Rate-limited to a maximum of 4 calls every 30 seconds
 */
export default function useDescriptionReady(onReady: (text: string) => void) {
  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;
  const callTimestampsRef = useRef<number[]>([]);
  const prevWordCountRef = useRef<number>(0);

  const tryInvoke = useCallback(
    (text: string) => {
      const now = Date.now();
      const windowMs = 30000;
      const maxCalls = 4;

      // prune old timestamps
      const recent = callTimestampsRef.current.filter(
        ts => now - ts < windowMs
      );
      callTimestampsRef.current = recent;

      if (recent.length >= maxCalls) {
        return;
      }

      callTimestampsRef.current = [...callTimestampsRef.current, now];
      onReadyRef.current(text);
    },
    []
  );

  const debouncedInactivityCall = useMemo(
    () =>
      debounce((text: string) => {
        tryInvoke(text);
      }, 2000),
    [tryInvoke]
  );

  useEffect(() => {
    return () => {
      debouncedInactivityCall.cancel();
    };
  }, [debouncedInactivityCall]);

  const handleDescriptionChange = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      const wordCount = trimmed.length ? trimmed.split(/\s+/).length : 0;

      if (prevWordCountRef.current < 7 && wordCount >= 7) {
        tryInvoke(text);
        debouncedInactivityCall.cancel();
      } else {
        debouncedInactivityCall(text);
      }

      prevWordCountRef.current = wordCount;
    },
    [debouncedInactivityCall, tryInvoke]
  );

  return handleDescriptionChange;
}
